import {
  RISQ_PREP_TIMINGS,
  RISQ_WORK_TYPES,
  type RisqPrepTiming,
  type RisqWorkType,
} from './content';
import type { RisqLayerSource } from './layers';
import { DEFAULT_TIMING } from './prep';

/**
 * Types of work shown in the apps and filters, in display order. `company` ("action or supply
 * from the office") stays in the database but is not shown (owner, 2026-11-03, D72).
 */
export type VisibleWorkType = Exclude<RisqWorkType, 'company'>;

export const VISIBLE_WORK_TYPES: readonly VisibleWorkType[] = RISQ_WORK_TYPES.filter(
  (t): t is VisibleWorkType => t !== 'company',
);

export function visibleWorkTypes(types: readonly RisqWorkType[]): VisibleWorkType[] {
  return VISIBLE_WORK_TYPES.filter((t) => types.includes(t));
}

export interface QuestionFlags {
  workTypes: readonly RisqWorkType[];
  prepTiming: readonly RisqPrepTiming[];
}

export interface ResolvedFlags {
  /** Visible types of work (without `company`). */
  workTypes: VisibleWorkType[];
  /** Timing flags as stored (may be empty). */
  prepTiming: RisqPrepTiming[];
  /** Timing used for "what to do now" and progress: no flag = 3-m (Q27). */
  effectiveTiming: RisqPrepTiming[];
  /** The question has no timing flag, so 3-m is assumed. */
  timingIsDefault: boolean;
  source: Exclude<RisqLayerSource, 'vessel'>;
}

function inOrder<T extends string>(order: readonly T[], values: readonly T[]): T[] {
  return order.filter((v) => values.includes(v));
}

/**
 * Flags of a question for a company: the company layer when it has a row, else the base content
 * (master file, or Admin's change). Same rule as `private.risq_flags_value`.
 */
export function resolveFlags(base: QuestionFlags, company?: QuestionFlags | null): ResolvedFlags {
  const source = company ? 'company' : 'base';
  const flags = company ?? base;
  const prepTiming = inOrder(RISQ_PREP_TIMINGS, flags.prepTiming);
  return {
    workTypes: visibleWorkTypes(flags.workTypes),
    prepTiming,
    effectiveTiming: prepTiming.length === 0 ? [DEFAULT_TIMING] : prepTiming,
    timingIsDefault: prepTiming.length === 0,
    source,
  };
}
